import { TagBadge, TagName } from "@/components/tag";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { API_SPECIFICATION_MODEL, APPLICATION_GRAPH, KNOWLEDGE_BASE, LOCAL_PACKAGE, LOCAL_SEMANTIC_MODEL, VISUAL_MODEL, RDFS_MODEL, V1 } from "@dataspecer/core-v2/model/known-models";
import { LanguageString } from "@dataspecer/core/core/core-resource";
import { BookOpen, ChevronDown, ChevronRight, CircuitBoard, CloudDownload, Code, EllipsisVertical, FileText, Folder, FolderDown, History, Import, NotepadTextDashed, Pencil, Plus, RotateCw, Search, Shapes, Sparkles, Tag, Tags, Trash2, WandSparkles } from "lucide-react";
import { useCallback, useContext, useDeferredValue, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "next-themes";
import { useLocalStorage } from "usehooks-ts";
import { formatRelativeTime, getValidTime } from "./components/time";
import { Translate } from "./components/translate";
import { Button } from "./components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "./components/ui/dropdown-menu";
import { Skeleton } from "./components/ui/skeleton";
import { Input } from "./components/ui/input";
import { CreateNew } from "./dialog/create-new";
import { DeleteResource } from "./dialog/delete-resource";
import { ProjectWizard } from "./dialog/project-wizard/project-wizard";
import { RenameResourceDialog } from "./dialog/rename-resource";
import { ResourceDetail } from "./dialog/resource-detail";
import { useToggle } from "./hooks/use-toggle";
import { getHistoryLink, getKnowledgeBaseLink, ModelIcon, modelTypeToName } from "./known-models";
import { useBetterModal } from "./lib/better-modal";
import { ResourcesContext, ensurePackageWorksForDSE, modifyUserMetadata, requestLoadPackage } from "./package";
import { ModifyDocumentationTemplate } from "./dialog/modify-documentation-template";
import React from "react";
import { SortModelsContext } from "./components/sort-models";
import { ModifyRawDialog } from "./dialog/modify-raw";
import { Autolayout } from "./dialog/autolayout";
import { Tooltip, TooltipContent, TooltipTrigger } from "./components/ui/tooltip";
import { ReloadResource } from "./dialog/reload-resource";
import { AddImported } from "./dialog/add-imported";
import { stopPropagation } from "./utils/events";
import { ModifyTagsDialog } from "./dialog/modify-tags";

export function lng(text: LanguageString | undefined): string | undefined {
  return text?.["cs"] ?? text?.["en"];
}

const useSortIris = (iris: string[]) => {
  const { selectedOption } = React.useContext(SortModelsContext);
  const resources = useContext(ResourcesContext);
  return useMemo(() => {
    const toSort = iris.map(iri => resources[iri]!);
    toSort?.sort((a, b) => {
      if (!a || !b) return 0;
      if (selectedOption === "name-az") {
        return lng(a.userMetadata?.label)?.localeCompare(lng(b.userMetadata?.label) ?? "") ?? 0;
      } else if (selectedOption === "name-za") {
        return (lng(b.userMetadata?.label) ?? "").localeCompare(lng(a.userMetadata?.label) ?? "") ?? 0;
      } else if (selectedOption === "modification-new-first") {
        return new Date(b.metadata?.modificationDate ?? 0).getTime() - new Date(a.metadata?.modificationDate ?? 0).getTime();
      } else if (selectedOption === "modification-old-first") {
        return new Date(a.metadata?.modificationDate ?? 0).getTime() - new Date(b.metadata?.modificationDate ?? 0).getTime();
      } else if (selectedOption === "creation-new-first") {
        return new Date(b.metadata?.creationDate ?? 0).getTime() - new Date(a.metadata?.creationDate ?? 0).getTime();
      } else if (selectedOption === "creation-old-first") {
        return new Date(a.metadata?.creationDate ?? 0).getTime() - new Date(b.metadata?.creationDate ?? 0).getTime();
      }
      return 0;
    });

    return toSort.map(r => r?.iri);
  }, [iris, resources, selectedOption]);
};

const Row = React.memo(({ iri, parentIri, onTagSelect }: { iri: string, parentIri?: string, onTagSelect: (tag: string) => void }) => {
  const resources = useContext(ResourcesContext);
  const resource = resources[iri]!;
  const { t, i18n } = useTranslation();
  const { theme } = useTheme();

  const [isOpen, setIsOpen] = useState<boolean>(false);

  const open = useCallback(async () => {
    requestLoadPackage(iri);
    setIsOpen(true);
  }, [iri]);

  const detailModalToggle = useToggle();

  const openModal = useBetterModal();

  const subResources = useSortIris(resource.subResourcesIri ?? []);
  const creationDate = getValidTime(resource.metadata?.creationDate);
  const modificationDate = getValidTime(resource.metadata?.modificationDate);
  const fullDateFormat = new Intl.DateTimeFormat(i18n.language, { dateStyle: "full", timeStyle: "long" });

  return <li className="border-b first:border-y last:border-none">
    <div className="flex items-center space-x-4 hover:bg-accent">
      {resource.types.includes(LOCAL_PACKAGE) ? <div className="flex"><button className="cursor-pointer" onClick={stopPropagation(() => isOpen ? setIsOpen(false) : open())}>
        {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
      </button><Folder className="ml-1 text-gray-400" /></div> : <div><ModelIcon type={resource.types} /></div>}

      <div className="min-w-0 grow">
        <div className="font-medium">
          <Translate
            text={resource.userMetadata?.label}
            match={(t, isMatch, language) => <>
              <span className={isMatch ? "" : "text-muted-foreground"}>{t}</span>
              {!isMatch && <span className="ml-1 ">@{language}</span>}
            </>}
            fallback={modelTypeToName[resource.types[0]]}
          />
        </div>
        <div className="flex text-sm text-gray-500">
          <span className="truncate w-[4cm]" title={creationDate ? fullDateFormat.format(creationDate) : undefined}>
            {creationDate && t("created-relative", {val: formatRelativeTime(creationDate, i18n.language)})}
          </span>
          <span className="truncate w-[6cm]" title={modificationDate ? fullDateFormat.format(modificationDate) : undefined}>
            {modificationDate && t("changed-relative", {val: formatRelativeTime(modificationDate, i18n.language)})}
          </span>
          <span className="truncate">
            {resource.iri}
          </span>
        </div>
      </div>

      {resource.userMetadata?.tags?.toSorted((a, b) => a.localeCompare(b)).map(tag => <TagBadge key={tag} name={tag} onClick={() => onTagSelect(tag)} onRemove={() => modifyUserMetadata(iri, {tags: resource.userMetadata?.tags?.filter(value => value !== tag) ?? []})} />)}

      {resource.types.includes(APPLICATION_GRAPH) &&
        <Button asChild variant={"ghost"} onClick={stopPropagation()}>
          <a href={import.meta.env.VITE_BACKEND + "/generate/application?iri=" + encodeURIComponent(iri)}>
            {t("generate application")}
          </a>
        </Button>
      }
      {resource.types.includes(V1.PSM) && <Button variant={"ghost"} onClick={async event => {
        event.preventDefault();
        event.stopPropagation();

        await ensurePackageWorksForDSE(parentIri!);
        window.location.href = import.meta.env.VITE_DATA_SPECIFICATION_EDITOR + "/editor?data-specification=" + encodeURIComponent(parentIri ?? "") + "&data-psm-schema=" + encodeURIComponent(iri);
      }}>{t("open")}</Button>}
      {resource.types.includes(VISUAL_MODEL) && <Button asChild variant={"ghost"} onClick={stopPropagation()}><a href={import.meta.env.VITE_CME + "/diagram?package-id=" + encodeURIComponent(parentIri ?? "") + "&view-id=" + encodeURIComponent(iri) }>{t("open")}</a></Button>}
      {resource.types.includes(API_SPECIFICATION_MODEL) && <Button asChild variant={"ghost"} onClick={stopPropagation()}><a href={import.meta.env.VITE_API_SPECIFICATION_APPLICATION + "?package-iri=" + encodeURIComponent(parentIri ?? "") + "&model-iri=" + encodeURIComponent(iri) }>{t("open")}</a></Button>}
      {resource.types.includes(KNOWLEDGE_BASE) && <Button asChild variant={"ghost"} onClick={stopPropagation()}><a href={getKnowledgeBaseLink(parentIri ?? "", iri)}>{t("open")}</a></Button>}

      {resource.types.includes(LOCAL_PACKAGE) && (resource.userMetadata as any)?.importedFromUrl &&
        <Tooltip>
          <TooltipTrigger>
            <Button asChild variant="ghost" size="icon" className="shrink-0" onClick={stopPropagation(() => openModal(ReloadResource, { id: iri, parentId: parentIri ?? "" }))}>
              <span>
                <RotateCw className="w-4 h-4" />
              </span>
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>{t("data specification reload")}</p>
          </TooltipContent>
        </Tooltip>
      }

      {resource.types.includes(RDFS_MODEL) &&
        <Tooltip>
          <TooltipTrigger>
            <Button asChild variant="ghost" size="icon" className="shrink-0" onClick={stopPropagation(() => openModal(ReloadResource, { id: iri, parentId: parentIri ?? "" }))}>
              <span>
                <RotateCw className="w-4 h-4" />
              </span>
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>{t("data specification reload")}</p>
          </TooltipContent>
        </Tooltip>
      }

      {resource.types.includes(LOCAL_PACKAGE) &&
        <Tooltip>
          <TooltipTrigger>
            <Button asChild variant="ghost" size="icon" className="shrink-0" onClick={stopPropagation()}>
              <a
                href={import.meta.env.VITE_DATA_SPECIFICATION_EDITOR + "/specification?dataSpecificationIri=" + encodeURIComponent(iri ?? "")}
                onClick={async event => {
                  event.preventDefault();
                  event.stopPropagation();

                  await ensurePackageWorksForDSE(iri);
                  window.location.href = import.meta.env.VITE_DATA_SPECIFICATION_EDITOR + "/specification?dataSpecificationIri=" + encodeURIComponent(iri ?? "");
                }}
              >
                <Code className="w-4 h-4" />
              </a>
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>{t("data specification button")}</p>
          </TooltipContent>
        </Tooltip>
      }

      {resource.types.includes(LOCAL_PACKAGE) &&
        <Tooltip>
          <TooltipTrigger>
            <Button asChild variant="ghost" size="icon" className="shrink-0" onClick={stopPropagation()}>
              <a href={import.meta.env.VITE_CME + "/diagram?package-id=" + encodeURIComponent(iri ?? "")}>
                <Shapes className="w-4 h-4" />
              </a>
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>{t("conceptual editor button")}</p>
          </TooltipContent>
        </Tooltip>
      }

      {resource.types.includes(LOCAL_PACKAGE) &&
        <Tooltip>
          <TooltipTrigger>
            <Button asChild variant="ghost" size="icon" className="relative shrink-0" onClick={stopPropagation()}>
              <a href={getHistoryLink(iri ?? "")}>
                <History className="w-4 h-4" />
                {resource.hasPendingEvolution &&
                  <span aria-hidden="true" className="absolute top-1.5 right-1.5 size-2 rounded-full bg-red-500"></span>
                }
              </a>
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>{t(resource.hasPendingEvolution ? "history button pending evolution" : "history button")}</p>
          </TooltipContent>
        </Tooltip>
      }

      {resource.types.includes(LOCAL_PACKAGE) &&
        <Button variant="ghost" size="icon" className="shrink-0" onClick={stopPropagation(() => openModal(CreateNew, { iri }))}>
          <Plus className="w-4 h-4" />
        </Button>
      }

      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="shrink-0">
            <EllipsisVertical className="w-4 h-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          {resource.types.includes(LOCAL_PACKAGE) && <DropdownMenuItem asChild><a href={import.meta.env.VITE_BACKEND + "/experimental/output.zip?iri=" + encodeURIComponent(iri)}><FolderDown className="w-4 h-4 mr-2" /> {t("export-zip")}</a></DropdownMenuItem>}
          {resource.types.includes(LOCAL_PACKAGE) && <DropdownMenuItem asChild><a target="_blank" href={import.meta.env.VITE_BACKEND + `/preview/${i18n.language}/index.html?iri=` + encodeURIComponent(iri)}><FileText className="w-4 h-4 mr-2" /> {t("show-documentation")} ({i18n.language})</a></DropdownMenuItem>}
          {i18n.language !== "en" && resource.types.includes(LOCAL_PACKAGE) && <DropdownMenuItem asChild><a target="_blank" href={import.meta.env.VITE_BACKEND + `/preview/en/index.html?iri=` + encodeURIComponent(iri)}><FileText className="w-4 h-4 mr-2" /> {t("show-documentation")} (en)</a></DropdownMenuItem>}
          {resource.types.includes(LOCAL_PACKAGE) && <DropdownMenuItem onClick={() => openModal(ModifyDocumentationTemplate, { iri })}><NotepadTextDashed className="w-4 h-4 mr-2" /> {t("modify-documentation-template")}</DropdownMenuItem>}
          {resource.types.includes(LOCAL_PACKAGE) && <DropdownMenuItem onClick={() => openModal(AddImported, { id: iri, urlOnly: true })}><Import className="w-4 h-4 mr-2" /> {t("import specification from url")}</DropdownMenuItem>}
          {resource.types.includes(LOCAL_PACKAGE) && <DropdownMenuItem asChild><a href={import.meta.env.VITE_CONTROLLED_VOCABULARY_MANAGER + "/" + encodeURIComponent(iri) + "?language=" + encodeURIComponent(i18n.language) + "&theme=" + encodeURIComponent(theme ?? "system")}><BookOpen className="w-4 h-4 mr-2" /> {t("controlled-vocabularies")}</a></DropdownMenuItem>}
          <DropdownMenuItem asChild><a href={import.meta.env.VITE_BACKEND + "/resources/export.zip?iri=" + encodeURIComponent(iri)}><CloudDownload className="w-4 h-4 mr-2" /> {t("export")}</a></DropdownMenuItem>
          <DropdownMenuItem onClick={async () => {
            const result = await openModal(RenameResourceDialog, { inputLabel: resource.userMetadata?.label, inputDescription: resource.userMetadata?.description });
            if (result) {
              await modifyUserMetadata(iri, { label: result.name, description: result.description });
            }
          }}><Pencil className="w-4 h-4 mr-2" /> {t("rename-resource.rename")}</DropdownMenuItem>
          <DropdownMenuItem onClick={async () => {
            const result = await openModal(ModifyTagsDialog, {tags: resource.userMetadata?.tags ?? []});
            if (result) await modifyUserMetadata(iri, {tags: result});
          }}><Tags className="w-4 h-4 mr-2" /> {t("modify-tags.title")}</DropdownMenuItem>
          {resource.types.includes(LOCAL_SEMANTIC_MODEL) && <DropdownMenuItem onClick={() => openModal(Autolayout, {iri, parentIri: parentIri!})}><Sparkles className="w-4 h-4 mr-2" /> {t("autolayout")}</DropdownMenuItem>}
          <DropdownMenuItem onClick={() => openModal(ModifyRawDialog, {iri})}><CircuitBoard className="w-4 h-4 mr-2" /> {t("modify raw data")}</DropdownMenuItem>
          <DropdownMenuItem className="bg-destructive text-destructive-foreground hover:bg-destructive" onClick={() => openModal(DeleteResource, {iri})}><Trash2 className="w-4 h-4 mr-2" /> {t("remove")}</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
    {subResources.length > 0 && isOpen && <ul className="pl-8">
      {subResources.map(iri => <Row iri={iri} key={iri} parentIri={resource.iri} onTagSelect={onTagSelect} />)}
    </ul>}
    <ResourceDetail isOpen={detailModalToggle.isOpen} close={detailModalToggle.close} iri={iri} />
  </li>
});

export default function Component() {
  return (
    <div>
      <RootPackage iri={"http://dataspecer.com/packages/local-root"} />
    </div>
  )
}

function RootPackage({iri}: {iri: string}) {
  const openModal = useBetterModal();
  const resources = useContext(ResourcesContext);
  const pckg = resources[iri];
  const { t } = useTranslation();

  // Whether the package is open or not
  const isOpen = true;
  const [search, setSearch] = useState("");
  const [selectedTagRaw, setSelectedTag] = useLocalStorage("filter-by-tag", "");
  const selectedTag = typeof selectedTagRaw === "string" ? selectedTagRaw : "";
  const deferredTag = useDeferredValue(selectedTag);
  const deferredSearch = useDeferredValue(search);
  const searchPattern = useMemo(() => {
    try {
      return new RegExp(deferredSearch, "i");
    } catch {
      return null;
    }
  }, [deferredSearch]);

  useEffect(() => {
    requestLoadPackage(iri);
  }, [iri]);

  const subResources = useSortIris(pckg?.subResourcesIri ?? []);
  const searchableResources = useMemo(() => subResources.map(iri => {
    const resource = resources[iri];
    return {
      iri,
      tags: resource?.userMetadata?.tags ?? [],
      titles: Object.values(resource?.userMetadata?.label ?? {}).map(title => ({
        value: title,
        normalized: title.toLocaleLowerCase(),
      })),
    };
  }), [subResources, resources]);
  const availableTags = useMemo(() => Array.from(new Set(
    searchableResources.flatMap(resource => resource.tags).filter(tag => tag !== "")
  )).sort((a, b) => a.localeCompare(b)), [searchableResources]);
  const normalizedSearch = deferredSearch.toLocaleLowerCase();
  const matchingResources = useMemo(() => searchableResources.filter(resource =>
    (deferredTag === "" || resource.tags.includes(deferredTag)) &&
    (deferredSearch === "" || resource.titles.some(title => searchPattern
      ? searchPattern.test(title.value)
      : title.normalized.includes(normalizedSearch)))
  ).map(resource => resource.iri), [searchableResources, deferredSearch, deferredTag, searchPattern, normalizedSearch]);
  const hasFilters = search !== "" || selectedTag !== "";
  const clearFilters = () => {
    setSearch("");
    setSelectedTag("");
  };

  if (pckg === null) {
    return;
  }

  if (!pckg) {
    return <div className="flex my-2">
      <Skeleton className="w-[44px] h-[44px] rounded-full" />
      <div className="ml-3">
        <Skeleton className="w-[200px] h-6 mb-1" />
        <Skeleton className="w-[100px] h-4" />
      </div>
    </div>;
  }

  return <div className="mb-12">
    <div className="flex flex-row items-center pb-2">
      <div className="flex items-baseline grow">
        <h2 className="text-2xl font-semibold tracking-tight font-heading scroll-m-20 first:mt-0">
          <Translate
            text={pckg.userMetadata?.label}
            match={(t, isMatch, language) => <>
              <span className={isMatch ? "" : "text-muted-foreground"}>{t}</span>
              {!isMatch && <span className="ml-1 ">@{language}</span>}
            </>}
          />
        </h2>
        <div aria-live="polite" aria-atomic="true" className="ml-5 mxtext-sm text-muted-foreground">
          {hasFilters && t("filter results", {count: matchingResources.length, total: subResources.length})}
        </div>
      </div>
      <div role="search" aria-label={t("search titles")} className="flex items-center min-w-0 gap-2 ml-8">
        <div className="relative w-64 min-w-0">
          <Search aria-hidden="true" className="absolute w-4 h-4 pointer-events-none left-3 top-2 text-muted-foreground" />
          <Input
            className="h-8 pl-9"
            type="search"
            aria-label={t("search titles")}
            placeholder={t("search titles")}
            value={search}
            onChange={event => setSearch(event.target.value)}
          />
        </div>
        <Select
          value={selectedTag === "" ? "all" : `tag:${selectedTag}`}
          onValueChange={value => setSelectedTag(value === "all" ? "" : value.slice(4))}
        >
          <SelectTrigger aria-label={t("filter by tag")} className="w-auto h-8 gap-2 min-w-40 shrink-0 whitespace-nowrap">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">
              <span className="inline-flex items-center gap-2">
                <Tag aria-hidden="true" className="w-4 h-4 text-muted-foreground" />
                {t("all tags")}
              </span>
            </SelectItem>
            {selectedTag !== "" && !availableTags.includes(selectedTag) &&
              <SelectItem value={`tag:${selectedTag}`} disabled textValue={selectedTag}>
                <TagName name={selectedTag} />
              </SelectItem>
            }
            {availableTags.map(tag => <SelectItem key={tag} value={`tag:${tag}`} textValue={tag}>
              <TagName name={tag} />
            </SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <Button variant="ghost" size="sm" className="ml-4 shrink-0"
        onClick={() => openModal(AddImported, { id: iri })}>
        <Import className="w-4 h-4 mr-2" /> {t("import")}
      </Button>
      <Button variant="default" size={"sm"} className="ml-4 shrink-0" onClick={() => openModal(ProjectWizard, {iri, defaultTag: selectedTag || undefined})}><WandSparkles className="w-4 h-4 mr-2" /> {t("project-wizard")}</Button>
    </div>
    {isOpen && <>
      {matchingResources.length > 0 ? <ul>
        {matchingResources.map(resourceIri => <Row iri={resourceIri} parentIri={iri} key={resourceIri} onTagSelect={setSelectedTag} />)}
      </ul> : subResources.length === 0 ? <div className="px-4 py-10 text-center border border-dashed rounded-lg">
        <FileText aria-hidden="true" className="w-6 h-6 mx-auto mb-3 text-muted-foreground" />
        <p className="font-medium">{t("no specifications")}</p>
        <p className="mt-1 text-sm text-muted-foreground">{t("create first specification")}</p>
        <div className="flex flex-wrap justify-center gap-2 mt-4">
          <Button variant="outline" onClick={() => openModal(AddImported, {id: iri})}>
            <Import aria-hidden="true" className="w-4 h-4 mr-2" /> {t("import")}
          </Button>
          <Button onClick={() => openModal(ProjectWizard, {iri, defaultTag: selectedTag || undefined})}>
            <WandSparkles aria-hidden="true" className="w-4 h-4 mr-2" /> {t("project-wizard")}
          </Button>
        </div>
      </div> : hasFilters ? <div className="px-4 py-10 text-center border border-dashed rounded-lg">
        <Search aria-hidden="true" className="w-6 h-6 mx-auto mb-3 text-muted-foreground" />
        <p className="font-medium">{t("no search results")}</p>
        <p className="mt-1 text-sm text-muted-foreground">{t("try other filters")}</p>
        <Button variant="outline" className="mt-4" onClick={clearFilters}>{t("clear filters")}</Button>
      </div> : null}
    </>}
  </div>;
}
